import { addMethod, array as yupArray, string as yupString } from 'yup';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import emojiRegex from 'emoji-regex';

/**
 * array.uniqueAtPath(path: string, message: string)
 *
 * Validates that all elements of the array have unique values at the given path.
 * Inspired by https://github.com/jquense/yup/issues/345#issuecomment-506341185.
 *
 * Usage:
 *   array()
 *     .of(
 *       object().shape({
 *         id: number(),
 *         name: string(),
 *       })
 *     )
 *     .uniqueAtPath('id', 'Ids must be unique');
 */
addMethod(yupArray, 'uniqueAtPath', function uniqueAtPath(path, message) {
  return this.test('uniqueAtPath', message, function validate(arr) {
    // If there is no array to check, treat it the same as an empty array (valid by default).
    if (!arr) {
      return true;
    }

    const getValueAtPath = object => get(object, path);
    const uniqueArray = uniq(arr.map(getValueAtPath));
    const isUnique = arr.length === uniqueArray.length;
    if (isUnique) {
      return true;
    }

    // Set the error message on the first offense of the uniqueness validation.
    const errorIndex = arr.findIndex((object, i) => getValueAtPath(object) !== uniqueArray[i]);
    return this.createError({ path: [this.path, errorIndex, path].join('.'), message });
  });
});

const emailRegex =
  // eslint-disable-next-line
  /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

addMethod(yupString, 'noEmailValue', function noEmailValue(message) {
  return this.test('noEmailValue', message, function validate(value) {
    return !emailRegex.test(value);
  });
});

const emojisRegex = emojiRegex();

addMethod(yupString, 'noEmojis', function noEmojis(message) {
  return this.test('noEmojis', message, function validate(value) {
    const withEmojis = /\p{Extended_Pictographic}/gu;
    const hasEmojis = withEmojis.test(value) || emojisRegex.test(value);
    return !hasEmojis;
  });
});

addMethod(yupString, 'validLength', function validLength(message, minVal) {
  return this.test('validLength', message, value => {
    return typeof value === 'string' && value?.trim().length > minVal;
  });
});
